import { useEffect, useMemo, useState } from 'react'
import { Accept, useDropzone } from 'react-dropzone'
import './file-uploader.scss'
import { Text } from '@radix-ui/themes'

const baseStyle = {
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  padding: '20px',
  borderWidth: 2,
  borderRadius: 2,
  borderColor: '#eeeeee',
  borderStyle: 'dashed',
  backgroundColor: '#fafafa',
  color: '#bdbdbd',
  outline: 'none',
  transition: 'border .24s ease-in-out',
}

const focusedStyle = {
  borderColor: '#2196f3',
}

const acceptStyle = {
  borderColor: '#00e676',
}

const rejectStyle = {
  borderColor: '#ff1744',
}

export type FileWithPreview = {
  preview: string
  file: File
}

type FileUploaderProps = {
  onFileDropped: (file: FileWithPreview) => void
  formLabel?: string
  errorMessage?: string | null
  showImageAsPreview?: boolean
  maxFiles?: number
  acceptTypes?: Accept
}

export const FileUploader = ({
  onFileDropped,
  formLabel,
  errorMessage,
  maxFiles,
  acceptTypes,
  showImageAsPreview,
}: FileUploaderProps) => {
  const [file, setFile] = useState<FileWithPreview>()

  const { getRootProps, getInputProps, isFocused, isDragAccept, isDragReject } = useDropzone({
    maxFiles: maxFiles ?? 1,
    accept: acceptTypes ?? { 'image/*': [] },
    onDrop: (acceptedFiles) => {
      const file = acceptedFiles[0]
      const fileWithPreview = { file, preview: URL.createObjectURL(file) }
      setFile(fileWithPreview)
      onFileDropped(fileWithPreview)
    },
  })

  useEffect(() => {
    // Make sure to revoke the data uris to avoid memory leaks, will run on unmount
    return () => file && URL.revokeObjectURL(file.preview)
  }, [file])

  const thumbnailFallback = file ? <span>{file.file.name}</span> : null

  const thumbnailPreview = file ? (
    <div className="thumbnail" key={file.file.name}>
      <div className="thumbnail-inner">
        <img
          alt="preview-file"
          src={file.preview}
          className="thumbnail-image"
          // Revoke data uri after image is loaded
          onLoad={() => {
            URL.revokeObjectURL(file.preview)
          }}
        />
      </div>
    </div>
  ) : null

  const thumbnail =
    (file && showImageAsPreview === true) || showImageAsPreview === undefined
      ? thumbnailPreview
      : thumbnailFallback

  const style = useMemo(
    () => ({
      ...baseStyle,
      ...(isFocused ? focusedStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
    }),
    [isFocused, isDragAccept, isDragReject],
  )

  return (
    <div className="container">
      {formLabel ? <Text>{formLabel}</Text> : null}
      <div {...getRootProps({ style })}>
        <input {...getInputProps()} />
        <p>Drag &apos;n&apos; drop some files here, or click to select files</p>
      </div>
      <aside className="thumbnail-container">{thumbnail}</aside>
      {errorMessage ? <Text>{errorMessage}</Text> : null}
    </div>
  )
}
